import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Car, Smartphone, Plane, ShoppingBag, CheckCircle2, XCircle, MinusCircle, ShieldCheck, Plus, Trash2, Scale, HelpCircle } from "lucide-react";

const SCENARIOS = [
  { id: "location_voiture", label: "Location voiture", icon: Car },
  { id: "achat_smartphone", label: "Smartphone", icon: Smartphone },
  { id: "voyage", label: "Voyage", icon: Plane },
  { id: "autre", label: "Autre", icon: ShoppingBag },
];

const PLACEHOLDER = {
  location_voiture: "Ex : Sixt propose le rachat de franchise à 18 €/jour pour 5 jours",
  achat_smartphone: "Ex : Apple Store propose AppleCare+ casse à 149 €",
  voyage: "Ex : assurance annulation à 39 € avec mon billet d'avion",
  autre: "Ex : extension de garantie 3 ans à 59 € sur un lave-linge",
};

const VERDICT = {
  "INUTILE": { icon: XCircle, cls: "text-destructive", bg: "bg-destructive/10 border-destructive/30", tag: "bg-destructive text-destructive-foreground" },
  "À VÉRIFIER": { icon: HelpCircle, cls: "text-ochre", bg: "bg-ochre/10 border-ochre/30", tag: "bg-ochre text-white" },
  "CONSEILLÉ": { icon: CheckCircle2, cls: "text-emerald-600", bg: "bg-emerald-500/10 border-emerald-500/30", tag: "bg-emerald-600 text-white" },
  "DÉCONSEILLÉ": { icon: MinusCircle, cls: "text-ochre", bg: "bg-ochre/10 border-ochre/30", tag: "bg-ochre text-white" },
};

let uid = 1;

export default function SimulatorView() {
  const [offers, setOffers] = useState([{ key: uid++, scenario: "location_voiture", details: "" }]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [analyzed, setAnalyzed] = useState([]);
  const [answers, setAnswers] = useState({});
  const [resolving, setResolving] = useState(false);

  const addOffer = () => setOffers((o) => [...o, { key: uid++, scenario: "location_voiture", details: "" }]);
  const removeOffer = (key) => setOffers((o) => o.filter((x) => x.key !== key));
  const update = (key, patch) => setOffers((o) => o.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  const analyze = async () => {
    const valid = offers.filter((o) => o.details.trim());
    if (valid.length === 0) { toast.error("Décrivez au moins une offre"); return; }
    setLoading(true);
    setResults(null);
    setAnswers({});
    const payload = valid.map((o) => ({ scenario: o.scenario, details: o.details }));
    setAnalyzed(payload);
    try {
      const { data } = await api.post("/simulator/compare", { offers: payload });
      setResults(data.results);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de l'évaluation");
    } finally {
      setLoading(false);
    }
  };

  const resolve = async () => {
    const result = results[0];
    const qs = result.questions || [];
    const off = analyzed[0] || {};
    const payloadAnswers = qs.map((q) => ({ question: q.text, answer: answers[q.id] ?? "" }));
    setResolving(true);
    try {
      const { data } = await api.post("/simulator/resolve", { scenario: off.scenario, details: off.details, answers: payloadAnswers });
      setResults([{ ...data, details: result.details }]);
      setAnswers({});
      toast.success("Verdict définitif calculé");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de la conclusion");
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="space-y-8 animate-in-up" data-testid="simulator-view">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Simulateur d'achat</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">On vous propose une assurance ?</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">Décrivez une ou plusieurs offres : l'IA vérifie si vous êtes déjà couvert et vous donne un verdict clair pour chacune, côte à côte.</p>
      </div>

      <div className="space-y-4">
        {offers.map((o, idx) => (
          <div key={o.key} data-testid={`offer-row-${idx}`} className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-wrap gap-2">
                {SCENARIOS.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`offer-${idx}-scenario-${s.id}`}
                    onClick={() => update(o.key, { scenario: s.id })}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition ${o.scenario === s.id ? "border-primary bg-accent text-accent-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}
                  >
                    <s.icon className="w-3.5 h-3.5" /> {s.label}
                  </button>
                ))}
              </div>
              {offers.length > 1 && (
                <button onClick={() => removeOffer(o.key)} className="w-8 h-8 grid place-items-center rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition" data-testid={`remove-offer-${idx}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <textarea
              data-testid={`offer-details-${idx}`}
              value={o.details}
              onChange={(e) => update(o.key, { details: e.target.value })}
              rows={2}
              placeholder={PLACEHOLDER[o.scenario]}
              className="mt-3 w-full p-3.5 rounded-xl border border-input bg-background text-sm outline-none focus:ring-2 focus:ring-ring/40 resize-none"
            />
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button onClick={addOffer} data-testid="add-offer-btn" className="flex items-center gap-2 h-11 px-4 rounded-xl border border-border text-sm font-medium hover:bg-muted transition">
          <Plus className="w-4 h-4" /> Ajouter une offre à comparer
        </button>
        <button onClick={analyze} disabled={loading} data-testid="simulator-submit-btn" className="flex items-center gap-2 h-11 px-5 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 disabled:opacity-60 transition">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />}
          {offers.length > 1 ? "Comparer les offres" : "Analyser l'offre"}
        </button>
      </div>

      {results && (
        <div className={`grid gap-5 ${results.length > 1 ? "md:grid-cols-2" : ""}`} data-testid="simulator-result">
          {results.map((result, i) => {
            const v = VERDICT[result.verdict] || VERDICT["CONSEILLÉ"];
            return (
              <div key={i} className={`min-w-0 rounded-2xl border p-6 animate-in-up ${v.bg}`}>
                <p className="text-xs font-mono text-muted-foreground mb-3 break-words line-clamp-2">{result.details}</p>
                <div className="flex items-center gap-3 flex-wrap">
                  <v.icon className={`w-7 h-7 shrink-0 ${v.cls}`} />
                  <span className={`text-sm font-bold uppercase tracking-wide px-3 py-1 rounded-full ${v.tag}`} data-testid="verdict-badge">{result.verdict}</span>
                  <span className="text-xs font-mono text-muted-foreground ml-auto">{result.confidence || "moyenne"}</span>
                </div>
                <p className="text-sm mt-4 leading-relaxed break-words">{result.reason}</p>
                {result.already_covered_by?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-[11px] font-mono uppercase tracking-wide text-muted-foreground mb-1.5">Déjà couvert par</p>
                    <div className="flex flex-wrap gap-2">
                      {result.already_covered_by.map((c, j) => (
                        <span key={j} className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-card border border-border break-words">
                          <ShieldCheck className="w-3 h-3 shrink-0 text-emerald-600" /> {c}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {results.length === 1 && result.verdict === "À VÉRIFIER" && result.questions?.length > 0 ? (
                  <div className="mt-4 rounded-xl bg-ochre/10 border border-ochre/25 p-4" data-testid="verify-questions">
                    <p className="text-[11px] font-mono uppercase tracking-wide text-ochre mb-3 flex items-center gap-1.5">
                      <HelpCircle className="w-3.5 h-3.5" /> L'app vérifie pour vous — répondez ci-dessous
                    </p>
                    <div className="space-y-3">
                      {result.questions.map((q) => {
                        const opts = q.type === "boolean" ? ["Oui", "Non"] : (q.options || []);
                        return (
                          <div key={q.id} data-testid={`question-${q.id}`}>
                            <p className="text-sm font-medium break-words">{q.text}</p>
                            {q.type === "text" ? (
                              <input
                                value={answers[q.id] || ""}
                                onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                                className="mt-1.5 w-full h-10 px-3 rounded-lg border border-input bg-card text-sm outline-none focus:ring-2 focus:ring-ring/40"
                                data-testid={`answer-input-${q.id}`}
                              />
                            ) : (
                              <div className="mt-1.5 flex flex-wrap gap-2">
                                {opts.map((opt) => {
                                  const sel = answers[q.id] === opt;
                                  return (
                                    <button
                                      key={opt}
                                      data-testid={`answer-${q.id}-${opt}`}
                                      onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt }))}
                                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${sel ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:border-primary/40"}`}
                                    >
                                      {opt}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <button
                      data-testid="resolve-btn"
                      onClick={resolve}
                      disabled={resolving || !result.questions.every((q) => answers[q.id] !== undefined && String(answers[q.id]).trim() !== "")}
                      className="mt-4 flex items-center gap-2 h-10 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
                    >
                      {resolving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />}
                      Obtenir la réponse définitive
                    </button>
                  </div>
                ) : result.to_verify?.length > 0 ? (
                  <div className="mt-4 rounded-xl bg-ochre/10 border border-ochre/25 p-3">
                    <p className="text-[11px] font-mono uppercase tracking-wide text-ochre mb-1.5 flex items-center gap-1.5">
                      <HelpCircle className="w-3.5 h-3.5" /> À vérifier avant de décider
                    </p>
                    <ul className="space-y-1 text-sm text-foreground list-disc list-inside">
                      {result.to_verify.map((t, j) => <li key={j} className="break-words">{t}</li>)}
                    </ul>
                  </div>
                ) : null}
                {result.estimated_savings > 0 && (
                  <p className="mt-4 font-heading text-xl font-extrabold text-emerald-600 break-words">+ {Math.round(result.estimated_savings)} € économisés en refusant</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
